import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Building2,
  Gavel,
  Scale as ScaleIcon,
  Loader2,
  CheckCircle2,
  TrendingUp,
  Home,
  AlertTriangle,
  Sparkles,
  History,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const STEP_ICONS = {
  accurint: Search,
  tloxp: Search,
  pacer: Gavel,
  courtlistener: ScaleIcon,
};

/**
 * Sequential broker step visual — mirrors the 4 stages the backend simulates.
 * Each step transitions from `pending → running → done`. The last hex-digit
 * even/odd rule is applied server-side, but the animation is purely UX.
 */
const StepDot = ({ state, i }) => {
  const base = "w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all";
  if (state === "done") {
    return (
      <div className={cn(base, "bg-success/20 border-success text-success")}>
        <CheckCircle2 size={16} />
      </div>
    );
  }
  if (state === "running") {
    return (
      <div className={cn(base, "bg-accent-pink/20 border-accent-pink text-accent-pink")}>
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }
  return (
    <div className={cn(base, "bg-bg-primary border-border-default text-text-muted")}>
      <span className="text-[11px] jea-mono font-bold">{i + 1}</span>
    </div>
  );
};

const StepRow = ({ step, state, i }) => (
  <motion.div
    initial={{ opacity: 0, x: -6 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.2, delay: i * 0.05 }}
    className={cn(
      "flex items-center gap-3 p-3 rounded-[8px] border transition-colors",
      state === "running" && "border-accent-pink bg-accent-pink/5 shadow-pink-glow",
      state === "done" && "border-success/40 bg-success/5",
      state === "pending" && "border-border-default bg-bg-primary",
    )}
    data-testid={`lookback-step-${step.key}`}
    data-state={state}
  >
    <StepDot state={state} i={i} />
    <div className="flex-1 min-w-0">
      <div className="text-white text-[13px] font-semibold">{step.broker_name}</div>
      <div className="text-text-secondary text-[12px]">{step.tagline}</div>
    </div>
    <div
      className={cn(
        "text-[11px] font-semibold tracking-[0.06em] uppercase",
        state === "done" && "text-success",
        state === "running" && "text-accent-pink",
        state === "pending" && "text-text-muted",
      )}
    >
      {state === "done" ? "Done" : state === "running" ? "Querying" : "Queued"}
    </div>
  </motion.div>
);

const NoOpportunityResult = ({ result }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className="rounded-[12px] border border-border-default bg-bg-primary p-5"
    data-testid="lookback-result-none"
  >
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-bg-tertiary border border-border-default flex items-center justify-center shrink-0">
        <History size={18} className="text-text-secondary" />
      </div>
      <div>
        <h3 className="text-white font-semibold text-[16px]">No qualifying transaction found</h3>
        <p className="text-text-secondary text-[13px] mt-1 leading-6">{result.explanation}</p>
        <div className="mt-3 text-[12px] text-text-muted">
          Generated <span className="jea-date">{relativeTime(result.generated_at)}</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const OpportunityResult = ({ result }) => {
  const rows = [
    { label: "Discharge Date", value: shortDate(result.bankruptcy_discharge_date), mono: true },
    { label: "Transaction Date", value: shortDate(result.transaction_date), mono: true },
    { label: "Transaction Type", value: result.transaction_type },
    { label: "Property Address", value: result.property_address },
    { label: "Transaction Amount", value: centsToUSD(result.transaction_amount_cents), money: true },
    { label: "Original Judgment", value: centsToUSD(result.original_judgment_amount_cents), money: true },
    { label: "Accrued Interest", value: centsToUSD(result.accrued_interest_since_judgment_cents), money: true },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      className="rounded-[16px] border border-accent-pink/50 shadow-elevated overflow-hidden"
      data-testid="lookback-result-opportunity"
      style={{
        background:
          "linear-gradient(180deg, var(--color-accent-gradient-a) 0%, rgba(9,20,36,1) 100%)",
      }}
    >
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center">
            <Sparkles size={20} className="text-accent-pink" />
          </div>
          <div>
            <div className="jea-caption text-accent-pink">Recovery Opportunity</div>
            <h3 className="text-white font-bold text-[20px]">Post-discharge {result.transaction_type?.toLowerCase()} detected</h3>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {rows.map((r) => (
              <div key={r.label} className="flex flex-col">
                <div className="jea-caption text-[11px]">{r.label}</div>
                <div
                  className={cn(
                    "mt-1 text-[14px]",
                    r.money ? "jea-mono text-accent-pink font-bold" : "text-white",
                    r.mono && "jea-mono",
                  )}
                >
                  {r.value}
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-[12px] border border-accent-pink/40 bg-bg-primary p-5 flex flex-col justify-center">
            <div className="jea-caption text-[11px] text-accent-pink">Estimated Recovery</div>
            <div className="jea-mono text-accent-pink font-extrabold text-[28px] mt-1" data-testid="lookback-recovery-value">
              {centsToUSD(result.estimated_lien_recovery_amount_cents)}
            </div>
            <p className="text-text-secondary text-[12px] mt-2 leading-5">
              Judgment lien may attach to the sale/refi proceeds if the abstract was
              properly recorded before the transaction.
            </p>
          </div>
        </div>

        <div className="mt-6 border-t border-border-default pt-5">
          <div className="jea-caption mb-2">Recommended Next Steps</div>
          <ul className="space-y-2">
            {(result.recommended_actions || []).map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px] text-text-secondary leading-6">
                <TrendingUp size={14} className="text-accent-pink shrink-0 mt-[3px]" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

const LookbackPanel = ({ debtors = [] }) => {
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [debtorName, setDebtorName] = useState("");
  const [address, setAddress] = useState("");
  const [existingId, setExistingId] = useState("");
  const [running, setRunning] = useState(false);
  const [stepStates, setStepStates] = useState({}); // key → pending|running|done
  const [result, setResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get("/lookback/pricing");
        if (alive) setPricing(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setPricingLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await apiClient.get("/lookback/history?limit=10");
      setHistory(data.searches || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen) loadHistory();
  }, [historyOpen]);

  const applyPrefill = (value) => {
    setExistingId(value);
    if (!value) return;
    const d = debtors.find((x) => x.id === value);
    if (d) {
      setDebtorName(d.full_name || "");
      const a = d.current_address || {};
      const parts = [a.street, a.city, a.state, a.zip].filter(Boolean);
      setAddress(parts.join(", "));
    }
  };

  const startAnimation = () => {
    if (!pricing?.steps) return;
    const seq = pricing.steps.map((s) => s.key);
    const initial = {};
    seq.forEach((k) => (initial[k] = "pending"));
    setStepStates(initial);
    // ~400ms per step — matches backend simulate_broker_call
    seq.forEach((key, idx) => {
      setTimeout(() => {
        setStepStates((prev) => ({
          ...prev,
          [key]: "running",
          ...(idx > 0 ? { [seq[idx - 1]]: "done" } : {}),
        }));
      }, idx * 400);
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!debtorName.trim() || !address.trim()) {
      toast.error("Enter a debtor name and a property address.");
      return;
    }
    setResult(null);
    setRunning(true);
    startAnimation();
    try {
      const body = {
        debtor_full_name: debtorName.trim(),
        property_address: address.trim(),
        ...(existingId ? { existing_debtor_id: existingId } : {}),
      };
      const { data } = await apiClient.post("/lookback/search", body);
      // Ensure all steps flip to done when the response returns
      if (pricing?.steps) {
        const done = {};
        pricing.steps.forEach((s) => (done[s.key] = "done"));
        setStepStates(done);
      }
      setResult(data.result);
      if (historyOpen) loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
      setStepStates({});
    } finally {
      setRunning(false);
    }
  };

  const totalCents = pricing?.total_cents || 0;

  return (
    <JeaCard data-testid="lookback-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="jea-caption">Historical Recovery Tool</div>
          <h2 className="text-white font-bold text-[20px] mt-1">Lookback Search</h2>
          <p className="text-text-secondary text-[13px] mt-1 max-w-[640px] leading-6">
            Find post-discharge property sales or refinances where a surviving judgment lien
            could still be enforceable. Fires 4 brokers in sequence and returns a deterministic
            recovery report.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="h-9 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[12px] font-semibold inline-flex items-center gap-2 transition-colors"
          data-testid="lookback-history-toggle"
        >
          <History size={13} /> {historyOpen ? "Hide History" : "View History"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <form onSubmit={submit} className="space-y-4">
          {debtors && debtors.length > 0 && (
            <div>
              <label className="jea-caption block mb-1.5" htmlFor="lookback-existing">
                Prefill from an existing debtor (optional)
              </label>
              <select
                id="lookback-existing"
                value={existingId}
                onChange={(e) => applyPrefill(e.target.value)}
                className="jea-focus w-full h-11 px-3 rounded-[8px] bg-bg-tertiary border border-border-default text-white text-[14px] focus:outline-none"
                data-testid="lookback-existing-select"
              >
                <option value="">— Manual entry —</option>
                {debtors.map((d) => (
                  <option key={d.id} value={d.id}>{d.full_name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="jea-caption block mb-1.5" htmlFor="lookback-name">Debtor Full Name</label>
            <JeaInput
              id="lookback-name"
              placeholder="e.g. Jonathan Whitmore"
              value={debtorName}
              onChange={(e) => setDebtorName(e.target.value)}
              data-testid="lookback-input-name"
            />
          </div>
          <div>
            <label className="jea-caption block mb-1.5" htmlFor="lookback-address">Property Address</label>
            <JeaInput
              id="lookback-address"
              leftIcon={<Home size={14} />}
              placeholder="Street, City, State, ZIP"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="lookback-input-address"
            />
          </div>
          <div className="rounded-[8px] bg-bg-primary border border-border-default p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-warning shrink-0 mt-[2px]" />
            <p className="text-[12px] text-text-secondary leading-5">
              Charges apply per broker. The result set is <strong className="text-white">deterministic</strong> for
              the same debtor + address, so subsequent lookups on identical inputs will not
              incur additional charges.
            </p>
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <div className="jea-caption">Estimated Cost</div>
              {pricingLoading ? (
                <Skeleton className="h-6 w-20 mt-1" />
              ) : (
                <div className="jea-mono text-accent-pink font-bold text-[18px]" data-testid="lookback-total-cost">
                  {centsToUSD(totalCents)}
                </div>
              )}
            </div>
            <JeaButton
              type="submit"
              onClick={submit}
              loading={running}
              disabled={running || !debtorName || !address}
              className="gap-2"
              data-testid="lookback-submit"
            >
              {running ? (<><Loader2 size={14} className="animate-spin" /> Searching…</>)
                       : (<><Search size={14} /> Run Lookback</>)}
            </JeaButton>
          </div>
        </form>

        {/* Broker steps column */}
        <div className="space-y-2 lg:min-w-[300px]">
          <div className="jea-caption">Broker Sequence</div>
          {pricingLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : (
            (pricing?.steps || []).map((step, i) => (
              <StepRow
                key={step.key}
                step={step}
                state={stepStates[step.key] || "pending"}
                i={i}
              />
            ))
          )}
        </div>
      </div>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <div className="mt-6" data-testid="lookback-result">
            {result.outcome === "recovery_opportunity_detected"
              ? <OpportunityResult result={result} />
              : <NoOpportunityResult result={result} />}
          </div>
        )}
      </AnimatePresence>

      {/* History */}
      <AnimatePresence>
        {historyOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-6 overflow-hidden"
            data-testid="lookback-history"
          >
            <div className="border-t border-border-default pt-5">
              <div className="jea-caption mb-2">Recent Searches</div>
              {historyLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9" />)}
                </div>
              ) : history.length === 0 ? (
                <div className="text-text-muted text-[13px]">No prior searches yet.</div>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <div
                      key={h.id}
                      className="p-3 rounded-[8px] bg-bg-primary border border-border-default flex items-center justify-between gap-3"
                      data-testid={`lookback-history-row-${h.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-white font-semibold text-[13px] truncate">{h.debtor_full_name}</div>
                        <div className="text-text-muted text-[11px] truncate">{h.property_address}</div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className={cn(
                          "text-[11px] px-2 py-1 rounded-full uppercase font-bold tracking-[0.06em]",
                          h.outcome === "recovery_opportunity_detected"
                            ? "bg-accent-pink/15 text-accent-pink border border-accent-pink/40"
                            : "bg-bg-tertiary text-text-secondary border border-border-default"
                        )}>
                          {h.outcome === "recovery_opportunity_detected" ? "Opportunity" : "No Match"}
                        </div>
                        <div className="jea-date text-[12px] w-[130px] text-right">
                          {relativeTime(h.created_at)}
                        </div>
                        <div className="jea-mono text-accent-pink font-bold text-[12px] w-[70px] text-right">
                          {centsToUSD(h.cost_cents)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </JeaCard>
  );
};

export default LookbackPanel;
